import * as luxon from "luxon";
import { ServerReminderDescriptor } from "../src/entity/ServerReminder";
import { OptionPreset, RoleOnRSVPAddTime, SyncDirection } from "./sesh_types";

export type SeshPermission = (typeof Constants.SESH_PERMISSIONS)[number];

export class Constants {
	public static readonly DISCORD_LINK_PREFIX =
		"https://discord.com/channels/";
	public static readonly NUMBERED_EMOJIS = [
		"1️⃣",
		"2️⃣",
		"3️⃣",
		"4️⃣",
		"5️⃣",
		"6️⃣",
		"7️⃣",
		"8️⃣",
		"9️⃣",
		"🔟",
		"🇦",
		"🇧",
		"🇨",
		"🇩",
		"🇪",
	];

	public static readonly ALPHABET_EMOJIS = [
		"🇦",
		"🇧",
		"🇨",
		"🇩",
		"🇪",
		"🇫",
		"🇬",
		"🇭",
		"🇮",
		"🇯",
		"🇰",
		"🇱",
		"🇲",
		"🇳",
		"🇴",
		"🇵",
		"🇶",
		"🇷",
		"🇸",
		"🇹",
		"🇺",
		"🇻",
		"🇼",
		"🇽",
		"🇾",
		"🇿",
	];

	public static readonly GCAL_ENABLED = true;

	public static readonly ATTENDEE_ROLES_HELP_LINK = `https://sesh.fyi/manual/attendee_roles`;
	public static readonly NATIVE_EVENT_LOCATION_HELP_ID = `native-event-location`;
	public static readonly PLACEHOLDER_TOKENS_HELP_ID = `placeholder-tokens`;

	public static readonly LIVE_LIST_UPDATE_COOLDOWN_MS = 5 * 1000;
	public static readonly LIVE_LIST_PATREON_UPDATE_COOLDOWN_MS = 2 * 1000;
	public static readonly EVENT_REACTION_LOCK_TIMEOUT = 15 * 1000;
	public static readonly POLL_REMOVAL_KEY_EXPIRY = 3 * 1000;
	public static readonly PATREON_REMOVAL_GRACE_PERIOD_MS =
		1000 * 60 * 60 * 24;

	public static readonly QUEUE_RANDOM_OFFSET_MAX_MS = 10 * 1000;

	public static readonly POLL_INLINE_FORMAT_LIMIT = 10;

	public static readonly MESSAGE_SWEEP_INTERVAL = 3600;
	public static readonly MESSAGE_CACHE_LIFETIME = 21600;

	public static readonly WEB_UI_DROPDOWN_LIMIT = 20;

	public static readonly FAT_ESCAPE_COMMA = "，";

	public static readonly WEB_UI_DROPDOWN_FORM_CACHE_SIZE = 100;

	public static readonly MAX_POLL_OPTIONS = 15;

	public static readonly DELETE_EVENT_LIST_LIMIT = 7;

	public static readonly SECONDARY_TIMEZONE_LIMIT = 10;

	public static readonly AUTO_DELETE_MESSAGE_TIMEOUT = 300000;

	public static readonly LIVE_LIST_TIME_PER_VOTE = 24 * 60 * 60 * 1000;

	public static readonly MAX_GUILD_NAME_LENGTH_WEB_UI = 20;
	public static readonly MAX_GUILD_NAME_HEADER_LENGTH_WEB_UI = 30;
	public static readonly MAX_EVENT_NAME_LENGTH_WEB_UI = 16;

	public static readonly STOP_DM_MESSAGES_CODE = "STOP";
	public static readonly RESUME_DM_MESSAGES_CODE = "RESUME";

	public static readonly STOP_FOOTER_TEXT = `Use /settings in this DM to disable these messages`;

	public static readonly LIST_EMOJI = "📃";

	public static readonly LIST_EMBED_TITLE = `${Constants.LIST_EMOJI} Event Listings`;

	public static readonly SETTINGS_EMOJI = "⚙️";
	public static readonly EDIT_REACTION = "📝";
	public static readonly TIME_CONVERT_REACTION = "🕐";
	public static readonly STOP_EMOJI = "🛑";
	public static readonly CLEAR_EVENTS_FEED_EMOJI = "❌";

	public static readonly CONFIRM_DELETION_REACTION = "☑️";

	public static readonly RSVP_REACTION = "✅";
	public static readonly MAYBE_REACTION = "🤷";
	public static readonly NO_REACTION = "❌";

	public static readonly POLL_EMOJI = "📊";
	public static readonly POLL_TITLE_PREFIX = `${Constants.POLL_EMOJI}  `;

	public static readonly DELETE_EMOJI = "🗑️";

	public static readonly CONFIRM_EMOJI = "☑️";

	public static readonly LIVE_LIST_EMOJI = "🔄";

	public static readonly LIVE_LIST_PIN_EMOJI = "📍";

	public static readonly CANCEL_REMINDER_EMOJI = "🔕";

	public static readonly PERSONAL_RSVP_FEED_EMOJI = "📅";

	public static readonly PREMIUM_EMOJI = "💎";

	public static readonly DEFAULT_SIGNUP_OPTIONS_EMBED_EXPIRY = 300000; // 5 minutes

	public static readonly MAX_SIGNUP_OPTION_PRESETS = 100; // LIMITED by reaction limit

	public static readonly EVENT_CHAR_LIMIT = 64;
	public static readonly IMAGE_URL_LIMIT = 2048;

	// discord built in character limits for embeds
	public static readonly DESCRIPTION_CHAR_LIMIT = 4096;
	public static readonly TIME_FINDER_DESCRIPTION_CHAR_LIMIT = 2000;
	public static readonly NATIVE_EVENT_DESCRIPTION_CHAR_LIMIT = 900;
	public static readonly MAX_EMBEDS_PER_MESSAGE = 10;
	public static readonly FIELD_CHAR_LIMIT = 1024;
	public static readonly TOTAL_CHAR_LIMIT = 6000;
	public static readonly FIELD_LIMIT = 25;
	public static readonly NORMAL_MESSAGE_CHAR_LIMIT = 2000;
	public static readonly EMBED_TITLE_LIMIT = 256;
	public static readonly EMBED_FIELD_TITLE_LIMIT = 256;
	// We prefix poll options with custom emojis, so 256 - <custom_reaction_string.length>
	public static readonly POLL_OPTION_CHARACTER_LIMIT = 230;
	public static readonly THREAD_TITLE_LIMIT = 100;

	// limits for native events
	public static readonly TITLE_CHARACTER_LIMIT = 240;
	public static readonly LOCATION_CHARACTER_LIMIT = 100;

	public static readonly EMBED_COLOR = "#00f4ff";
	public static readonly ERROR_EMBED_COLOR = "#ff0000";

	public static readonly SESH_PERMISSIONS = [
		"create",
		"link",
		"list",
		"ai",
		"delete",
		"poll",
		"remind",
		"timestamp",
		"view_logs",
		"settings",
	] as const;

	public static readonly EVENT_NAME_PLACEHOLDER = "$EventName";

	// True = Everyone, False = Admins
	public static readonly COMMAND_DEFAULT_EVERYONE_PERMISSION: Record<
		SeshPermission,
		boolean
	> = {
		create: true,
		link: true,
		list: true,
		ai: true,
		delete: true,
		poll: true,
		timestamp: true,
		settings: false,
		view_logs: false,
		remind: true,
	};

	public static readonly COMMANDS_THAT_IGNORE_PERMISSIONS = [
		"activate",
		"help",
	];

	public static readonly PATREON_LINK = "https://www.patreon.com/tunks";
	public static readonly PREMIUM_LINK = "https://sesh.fyi/premium";

	public static readonly TOP_GG_VOTE_LINK =
		"https://top.gg/bot/616754792965865495/vote";

	public static readonly ONE_MINUTE_IN_MS = 60000;

	public static readonly QUEUE_JOB_TIMEOUT = Constants.ONE_MINUTE_IN_MS;

	public static readonly QUEUE_JOB_ATTEMPTS = 2;

	public static readonly ADMIN_IDS = [
		"218864400407199744",
		"241022648690147338",
	];

	// in seconds
	public static readonly EPHEMERAL_INTERACTION_REDIS_DATA_EXPIRY =
		60 * 60 * 1; // 1 hour

	public static readonly DEFAULT_EVENT_DURATION = { hours: 1 };

	public static readonly luxon_reminder_offset_definitions = [
		luxon.Duration.fromObject({ seconds: 0 }),
		luxon.Duration.fromObject({ minutes: 10 }),
		luxon.Duration.fromObject({ hours: 1 }),
		luxon.Duration.fromObject({ days: 1 }),
		luxon.Duration.fromObject({ days: 7 }),
	];

	public static readonly MANAGE_GUILD_BIT_CODE = 0x00000020;
	public static readonly ADMIN_BIT_CODE = 0x00000008;

	public static readonly ONE_DAY_IN_SECONDS = 1 * 24 * 60 * 60;

	public static readonly ONE_DAY_IN_MILLISECONDS =
		Constants.ONE_DAY_IN_SECONDS * 1000;

	// Should just hardcode the backoff times tbh
	public static readonly EVENT_RETRY_ATTEMPTS = 20;
	public static readonly EVENT_BASE_RETRY_TIME_IN_MINUTES = 0.1;
	public static readonly EVENT_EXPONENTIAL_BASE = 1.7;
	public static readonly EVENT_MAX_RETRY_TIME_IN_MS = 604800000; // ms in a week
	public static readonly BACK_OFF_STRATEGY_PREFIX = "exponential_";

	public static readonly EVENT_REACTION_LIMIT = 16;
	public static readonly EVENT_REACTION_OPTION_NAME_LIMIT = 200;

	public static readonly DEFAULT_ADD_ROLE_ON_RSVP_ADD_TIME =
		RoleOnRSVPAddTime.OnEventStart;
	public static readonly DEFAULT_REMOVE_ROLE_ON_EVENT_END = true;

	public static readonly WEB_UI_DEFAULT_REACTION_PRESETS: OptionPreset[] = [
		{
			name: "Default",
			custom_options: [
				{
					emoji: "✅",
					name: "Attendees",
					has_reminders: true,
					limit: null,
					do_not_apply_attendee_role: false,
					user_ids: undefined,
				},
			],
			allow_multiple_rsvps: false,
		},
		{
			name: "Yes Maybe No",
			custom_options: [
				{
					emoji: "✅",
					name: "Attendees",
					has_reminders: true,
					limit: null,
					do_not_apply_attendee_role: false,
					user_ids: undefined,
				},
				{
					emoji: Constants.MAYBE_REACTION,
					name: "Maybe",
					has_reminders: true,
					limit: null,
					do_not_apply_attendee_role: true,
					user_ids: undefined,
				},
				{
					emoji: Constants.NO_REACTION,
					name: "No",
					has_reminders: false,
					limit: null,
					do_not_apply_attendee_role: true,
					user_ids: undefined,
				},
			],
			allow_multiple_rsvps: false,
		},
		{
			name: "MMO",
			custom_options: [
				{
					emoji: "🛡️",
					name: "Tank",
					has_reminders: true,
					limit: null,
					do_not_apply_attendee_role: false,
					user_ids: undefined,
				},
				{
					emoji: "⚔️",
					name: "DPS",
					has_reminders: true,
					limit: null,
					do_not_apply_attendee_role: false,
					user_ids: undefined,
				},
				{
					emoji: "💖",
					name: "Support",
					has_reminders: true,
					limit: null,
					do_not_apply_attendee_role: false,
					user_ids: undefined,
				},
			],
			allow_multiple_rsvps: false,
		},
	];

	public static readonly DEFAULT_EVENT_REACTIONS: OptionPreset =
		Constants.WEB_UI_DEFAULT_REACTION_PRESETS[0];

	public static readonly SESH_EMOJI_FARM_GUILD_ID = "733078138954973216";

	public static readonly SESH_CUSTOM_EMOJIS = {
		REMINDER_OPT_IN: {
			name: "reminder_opt_in",
			id: "841370468329717812",
			animated: false,
			guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
			guild_name: "sesh",
		},
		REMINDER_OPT_OUT: {
			name: "reminder_opt_out",
			id: "841370932479918161",
			animated: false,
			guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
			guild_name: "sesh",
		},
		LOADING: {
			name: "loading",
			id: "735657483951013888",
			animated: true,
			guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
			guild_name: "sesh",
		},
		// Source: twemoji (https://twemoji.twitter.com/). Edited by adding a black outline to increase contrast
		SESH_GEAR: {
			name: "sesh_gear",
			id: "951248549667016814",
			animated: false,
			guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
			guild_name: "sesh",
		},
		GCAL: {
			name: "GCal",
			id: "1042942721284911144",
			animated: false,
			guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
			guild_name: "sesh",
		},
		NOT_FOUND: [
			{
				name: "emoji_not_found",
				id: "733103217101111437",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "sesh",
			},
			{
				name: "emoji_not_found",
				id: "733116326490538054",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "sesh",
			},
			{
				name: "emoji_not_found",
				id: "733116278457499708",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "sesh",
			},
			{
				name: "emoji_not_found",
				id: "733116334866694256",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "sesh",
			},
			{
				name: "emoji_not_found",
				id: "733116348049129482",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "sesh",
			},
		],
		ALPHABET: [
			{
				name: "A_",
				id: "1047616462338150460",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "B_",
				id: "1047616463248314538",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "C_",
				id: "1047616464137502801",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "D_",
				id: "1047616465118969866",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "E_",
				id: "1047616466293366875",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "F_",
				id: "1047616467337748490",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "G_",
				id: "1047616468545716264",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "H_",
				id: "1047616469770444870",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "I_",
				id: "1047616470953246800",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "J_",
				id: "1047616472215720006",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "K_",
				id: "1047616473465626654",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "L_",
				id: "1047616474455474176",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "M_",
				id: "1047616475097206845",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "N_",
				id: "1047616476670087289",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "O_",
				id: "1047616477739626577",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "P_",
				id: "1047616478767226981",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "Q_",
				id: "1047616480000348252",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "R_",
				id: "1047616481187332187",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "S_",
				id: "1047616482349170728",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "T_",
				id: "1047616483540340857",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "U_",
				id: "1047616484697981099",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "V_",
				id: "1047616485708800132",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "W_",
				id: "1047616487411695656",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "X_",
				id: "1047616488510591076",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "Y_",
				id: "1047616489617883136",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
			{
				name: "Z_",
				id: "1047616490666479676",
				animated: false,
				guild_id: Constants.SESH_EMOJI_FARM_GUILD_ID,
				guild_name: "Alphabet",
			},
		],
	};

	public static readonly SESH_FYI_DISCORD_LINK = "https://discord.gg/hJgX7Hw";

	public static readonly CUSTOM_OPTION_PRESET_NAME_MAX_LENGTH = 64;

	public static readonly PREFIX_CHAR_LIMIT = 32;

	public static readonly SESH_INVITE_LINK =
		"https://discord.com/oauth2/authorize?client_id=616754792965865495&permissions=17998396517440&scope=applications.commands%20bot&response_type=code";

	public static readonly PENDING_EVENT_REDIS_KEY_EXPIRY = 5000;

	public static readonly SYNC_LIMITS = {
		[SyncDirection.Outbound]: {
			premium: 5,
			standard: 1,
		},
		[SyncDirection.Inbound]: {
			premium: 5,
			standard: 0,
		},
	};

	public static readonly GUILD_MEMBERS_SEARCH_LIMIT = 20;
	public static readonly LISTING_PAGINATION_LIMIT = 10;
	public static readonly POLL_VOTERS_PAGINATION_LIMIT = 20;

	public static readonly MODIFY_RSVPERS_LIMIT = 10;

	public static readonly DISCORD_PREMIUM_MANAGEMENT_FAQ_LINK =
		"https://support.discord.com/hc/en-us/articles/360017693772-Managing-Subscriptions-and-Billing-FAQ#h_01J1FZH9BCJZ73NWBB9RTMESMG";

	public static readonly APP_DIRECTORY_PREMIUM_LINK =
		"https://discord.com/application-directory/616754792965865495/premium";

	public static readonly MAX_SERVER_REMINDERS_COUNT = 5;

	public static readonly DEFAULT_EVENT_SERVER_REMINDERS: ServerReminderDescriptor[] =
		[
			{
				fire_before_ms: 0,
				message_title: `**$EventName** is starting $DT_TimeRelative!`,
				message_description: `**[Event Details]($EventLink)**`,
				role_mention_ids: [],
				user_mention_ids: [],
				has_fired: false,
				_type: "descriptor",
				desired_channel_id: null,
				sent_channel_id: null,
				sent_message_id: null,
			},
		];

	public static readonly MAXIMUM_EVENT_TEMPLATES = {
		free: 3,
		premium: 1000,
	};
	public static readonly EVENT_TEMPLATE_NAME_LIMITS = {
		min: 0,
		max: 100,
	};

	public static readonly DISCORD_BLURPLE = "#5865F2";

	public static readonly DISCORD_ONE_TIME_PURCHASE_SINGLE_SKU_ID =
		"1291458878835396630";
	public static readonly DISCORD_ONE_TIME_PURCHASE_BUNDLE_SKU_ID =
		"1291492651853676636";
}
